import { useId, useMemo, useRef } from 'react'
import { ArrowUp, Plus, X } from 'lucide-react'
import { Badge, Button, IconButton, Input, Select } from '../common'
import { ActivityIndicator } from '../common/feedback/ActivityIndicator'
import {
  CAPTION_KIND_OPTIONS,
  CAPTION_UPLOAD_ACCEPT,
  MAX_CAPTION_TRACKS,
  captionLabelForLang,
  captionTrackIssues,
  isValidCaptionLang,
} from './mediaAttachments'
import styles from './CaptionTracksEditor.module.css'

function trackSummary(tracks) {
  if (tracks.length === 0) return 'none'
  return tracks.map((track) => track.label || track.lang || 'Untitled').join(', ')
}

// Edits the sidecar caption tracks of one video attachment. The first track
// is the one the player shows when a viewer turns captions on.
function CaptionTracksEditor({
  mediaName,
  tracks = [],
  onUploadFile,
  onChange,
  uploading = false,
  disabled = false,
}) {
  const fileInputRef = useRef(null)
  const idPrefix = useId()
  const issuesByTrack = useMemo(() => new Map(
    captionTrackIssues(tracks).map((issue) => [issue.trackId, issue.message]),
  ), [tracks])
  const atLimit = tracks.length >= MAX_CAPTION_TRACKS

  const updateTrack = (trackId, changes) => {
    onChange?.(tracks.map((track) => (track.id === trackId ? { ...track, ...changes } : track)))
  }

  const changeLang = (track, value) => {
    const lang = value.toLocaleLowerCase()
    const previousAutoLabel = track.lang ? captionLabelForLang(track.lang) : ''
    const labelIsAutomatic = !track.label || track.label === previousAutoLabel
    updateTrack(track.id, {
      lang,
      ...(labelIsAutomatic ? { label: isValidCaptionLang(lang) ? captionLabelForLang(lang) : '' } : {}),
    })
  }

  const removeTrack = (trackId) => {
    onChange?.(tracks.filter((track) => track.id !== trackId))
  }

  const moveUp = (index) => {
    if (index <= 0) return
    const next = [...tracks]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    onChange?.(next)
  }

  const handleFileSelection = (event) => {
    const [file] = Array.from(event.target.files || [])
    event.target.value = ''
    if (file) onUploadFile?.(file)
  }

  return (
    <div className={styles.editor} role="group" aria-label={`Captions for ${mediaName}`}>
      <div className={styles.header}>
        <span className={styles.summary}>
          <span className={styles.summaryTitle}>Captions</span>
          {' · '}
          {trackSummary(tracks)}
        </span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading || atLimit}
          title={atLimit ? `A video can have at most ${MAX_CAPTION_TRACKS} caption tracks.` : undefined}
        >
          <Plus size={14} aria-hidden="true" />
          Add captions
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={CAPTION_UPLOAD_ACCEPT}
          onChange={handleFileSelection}
          aria-label={`Add captions for ${mediaName}`}
          className={styles.hiddenFileInput}
          disabled={disabled || uploading || atLimit}
        />
      </div>

      {uploading ? (
        <ActivityIndicator compact status="running" label="Uploading captions…" />
      ) : null}

      {tracks.length > 0 ? (
        <>
          <ol className={styles.trackList}>
            {tracks.map((track, index) => {
              const issue = issuesByTrack.get(track.id)
              const langId = `${idPrefix}-${track.id}-lang`
              const errorId = `${langId}-error`
              return (
                <li key={track.id} className={styles.track}>
                  <div className={styles.trackHeader}>
                    <span className={styles.fileName} title={track.fileName}>{track.fileName || 'Caption file'}</span>
                    {index === 0 ? <Badge compact tone="info">Default</Badge> : null}
                    <span className={styles.trackActions}>
                      {index > 0 ? (
                        <IconButton
                          variant="ghost"
                          label={`Move ${track.label || track.lang || 'caption track'} up`}
                          onClick={() => moveUp(index)}
                          disabled={disabled}
                        >
                          <ArrowUp size={14} aria-hidden="true" />
                        </IconButton>
                      ) : null}
                      <IconButton
                        variant="ghost"
                        label={`Remove ${track.label || track.lang || 'caption track'}`}
                        onClick={() => removeTrack(track.id)}
                        disabled={disabled}
                      >
                        <X size={14} aria-hidden="true" />
                      </IconButton>
                    </span>
                  </div>
                  <div className={styles.trackFields}>
                    <label className={styles.field} htmlFor={langId}>
                      <span className={styles.fieldLabel}>Language</span>
                      <Input
                        id={langId}
                        size="compact"
                        value={track.lang}
                        placeholder="en"
                        aria-label="Caption language"
                        aria-invalid={issue ? 'true' : undefined}
                        aria-describedby={issue ? errorId : undefined}
                        onChange={(event) => changeLang(track, event.target.value)}
                        disabled={disabled}
                      />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>Label</span>
                      <Input
                        size="compact"
                        value={track.label}
                        placeholder={track.lang ? captionLabelForLang(track.lang) : 'English'}
                        aria-label="Caption label"
                        maxLength={80}
                        onChange={(event) => updateTrack(track.id, { label: event.target.value })}
                        disabled={disabled}
                      />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>Kind</span>
                      <Select
                        size="compact"
                        value={track.kind}
                        aria-label="Caption kind"
                        onChange={(event) => updateTrack(track.id, { kind: event.target.value })}
                        disabled={disabled}
                      >
                        {CAPTION_KIND_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Select>
                    </label>
                  </div>
                  {issue ? (
                    <p id={errorId} className={styles.error} role="alert">
                      {`${issue.charAt(0).toUpperCase()}${issue.slice(1)}.`}
                    </p>
                  ) : null}
                </li>
              )
            })}
          </ol>
          <p className={styles.helper}>The first track is the one viewers see when they turn captions on.</p>
        </>
      ) : null}
    </div>
  )
}

export default CaptionTracksEditor
